import React, { useState, useEffect } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { verifyAuth, getStats, seedDatabase, changePassword } from '../../lib/api';
import { toast } from 'sonner';
import { Toaster } from '../ui/sonner';
import {
  LayoutDashboard,
  Package,
  Tags,
  Building2,
  Users,
  FileText,
  HelpCircle,
  LogOut,
  Menu,
  X,
  Home,
  Database,
  KeyRound,
} from 'lucide-react';
import { LOGO_SRC } from '../../lib/branding';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';

// Set to true to show the Seed Demo Data button in the admin sidebar
const SHOW_SEED_DEMO_DATA = false;

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    verifyAuth()
      .then(() => {
        getStats().then(res => setStats(res.data)).catch(console.error);
      })
      .catch(() => {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      });
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
    toast.success('Logged out successfully');
  };

  const handleSeedDatabase = async () => {
    if (seeding) return;
    setSeeding(true);
    try {
      await seedDatabase();
      toast.success('Database seeded successfully');
      getStats().then(res => setStats(res.data));
    } catch (error) {
      toast.error('Failed to seed database');
    } finally {
      setSeeding(false);
    }
  };

  const resetPasswordForm = () => {
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    const { currentPassword, newPassword, confirmPassword } = passwordForm;

    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New password and confirmation do not match');
      return;
    }
    if (currentPassword === newPassword) {
      toast.error('New password must be different from current password');
      return;
    }

    setChangingPassword(true);
    try {
      await changePassword(currentPassword, newPassword);
      toast.success('Password updated successfully');
      setPasswordDialogOpen(false);
      resetPasswordForm();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Failed to update password');
    } finally {
      setChangingPassword(false);
    }
  };

  const navItems = [
    { name: 'Dashboard', path: '/admin/dashboard', icon: LayoutDashboard },
    { name: 'Categories', path: '/admin/categories', icon: Tags },
    { name: 'Brands', path: '/admin/brands', icon: Building2 },
    { name: 'Products', path: '/admin/products', icon: Package },
    { name: 'Leads', path: '/admin/leads', icon: Users },
    { name: 'Blogs', path: '/admin/blogs', icon: FileText },
    { name: 'FAQs', path: '/admin/faqs', icon: HelpCircle },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#F4F4F5]" data-testid="admin-layout">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 admin-sidebar transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-gray-700">
            <img src={LOGO_SRC} alt="Silver Perfumes" className="h-10" />
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 overflow-y-auto">
            <ul className="space-y-1">
              {navItems.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.path}
                    className={`admin-nav-item flex items-center gap-3 px-4 py-3 text-sm ${
                      isActive(item.path)
                        ? 'active text-[#C0C4CC]'
                        : 'text-gray-300 hover:text-white'
                    }`}
                    onClick={() => setSidebarOpen(false)}
                    data-testid={`admin-nav-${item.name.toLowerCase()}`}
                  >
                    <item.icon className="h-5 w-5" />
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>

            {/* Seed Button — hidden via SHOW_SEED_DEMO_DATA */}
            {SHOW_SEED_DEMO_DATA && (
              <div className="mt-8 px-4">
                <button
                  onClick={handleSeedDatabase}
                  disabled={seeding}
                  className="flex items-center gap-2 text-sm text-gray-400 hover:text-[#C0C4CC] transition-colors w-full"
                  data-testid="seed-database-btn"
                >
                  <Database className="h-4 w-4" />
                  {seeding ? 'Seeding...' : 'Seed Demo Data'}
                </button>
              </div>
            )}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-gray-700">
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-2 text-sm text-gray-300 hover:text-white"
              data-testid="admin-view-site"
            >
              <Home className="h-5 w-5" />
              View Site
            </Link>
            <button
              onClick={() => {
                resetPasswordForm();
                setPasswordDialogOpen(true);
                setSidebarOpen(false);
              }}
              className="flex items-center gap-3 px-4 py-2 text-sm text-gray-300 hover:text-white w-full"
              data-testid="admin-change-password"
            >
              <KeyRound className="h-5 w-5" />
              Change Password
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-2 text-sm text-gray-300 hover:text-red-400 w-full"
              data-testid="admin-logout"
            >
              <LogOut className="h-5 w-5" />
              Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
          <div className="flex items-center justify-between px-6 py-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2"
              data-testid="admin-menu-toggle"
            >
              {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>

            <div className="flex items-center gap-4 ml-auto">
              {stats && (
                <div className="hidden md:flex gap-6 text-sm">
                  <span className="text-gray-500">
                    <span className="font-medium text-[#0A0A0A]">{stats.products}</span> Products
                  </span>
                  <span className="text-gray-500">
                    <span className="font-medium text-[#0A0A0A]">{stats.leads}</span> Leads
                  </span>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          <Outlet context={{ stats, refreshStats: () => getStats().then(res => setStats(res.data)) }} />
        </main>
      </div>

      <Dialog
        open={passwordDialogOpen}
        onOpenChange={(open) => {
          setPasswordDialogOpen(open);
          if (!open) resetPasswordForm();
        }}
      >
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4" data-testid="change-password-form">
            <div className="space-y-2">
              <Label htmlFor="current-password">Current Password</Label>
              <Input
                id="current-password"
                type="password"
                autoComplete="current-password"
                value={passwordForm.currentPassword}
                onChange={(e) =>
                  setPasswordForm({ ...passwordForm, currentPassword: e.target.value })
                }
                required
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                value={passwordForm.newPassword}
                onChange={(e) =>
                  setPasswordForm({ ...passwordForm, newPassword: e.target.value })
                }
                required
                minLength={8}
                data-testid="new-password-input"
              />
              <p className="text-xs text-gray-400">Minimum 8 characters</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm New Password</Label>
              <Input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                value={passwordForm.confirmPassword}
                onChange={(e) =>
                  setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })
                }
                required
                minLength={8}
                data-testid="confirm-password-input"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setPasswordDialogOpen(false)}
                disabled={changingPassword}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="btn-primary flex-1"
                disabled={changingPassword}
                data-testid="save-password-btn"
              >
                {changingPassword ? 'Updating…' : 'Update Password'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Toaster position="top-right" />
    </div>
  );
}
